import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dictionaries } from './translations';
import { Lang } from '@/data/constants';
import { brandCase } from '@/lib/brand';


interface LanguageCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Build a language-prefixed path: path('/jobs') -> '/es/trabajos' */
  path: (p: string, l?: Lang) => string;
  fading: boolean;
}

const Ctx = createContext<LanguageCtx | undefined>(undefined);

const STORAGE_KEY = 'bb_lang';

/** English path segment -> Spanish path segment (for localized SEO URLs). */
const SEGMENTS: Record<string, string> = {
  jobs: 'trabajos',
  pricing: 'precios',
  login: 'iniciar-sesion',
  register: 'registro',
  dashboard: 'panel',
  admin: 'administrador',
  privacy: 'privacidad',
  terms: 'terminos',
  'how-it-works': 'como-funciona',
  post: 'publicar',
  profile: 'perfil',
  'reset-password': 'restablecer-contrasena',
  contact: 'contacto',
  'my-leads': 'mis-leads',

  // --- programmatic SEO segments (trade / location / resource / core) ---
  trades: 'oficios',
  counties: 'condados',
  resources: 'recursos',
  about: 'acerca-de',
  memberships: 'membresias',
  'general-contractors': 'contratistas-generales',
  subcontractors: 'subcontratistas',
  homeowners: 'propietarios',
  commercial: 'comercial',

  'post-a-project': 'publicar-proyecto',
  faq: 'preguntas-frecuentes',
  help: 'ayuda',
  'project-directory': 'directorio-de-proyectos',
  'contractor-directory': 'directorio-de-contratistas',
  testimonials: 'testimonios',

};




export const toEsPath = (p: string) =>
  p
    .split('/')
    .map((seg) => SEGMENTS[seg] || seg)
    .join('/');

export const toEnPath = (p: string) => {
  const reverse: Record<string, string> = {};
  Object.entries(SEGMENTS).forEach(([k, v]) => (reverse[v] = k));
  return p
    .split('/')
    .map((seg) => reverse[seg] || seg)
    .join('/');
};

export const detectLang = (): Lang => {
  if (typeof window === 'undefined') return 'en';
  const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
  if (saved === 'en' || saved === 'es') return saved;
  const fromUrl = window.location.pathname.split('/')[1];
  if (fromUrl === 'es') return 'es';
  if (fromUrl === 'en') return 'en';
  const nav = (navigator.language || 'en').toLowerCase();
  return nav.startsWith('es') ? 'es' : 'en';
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(detectLang);
  const [fading, setFading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Keep <html lang>, title language and hreflang in sync
  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem(STORAGE_KEY, lang);
  }, [lang]);

  // If the URL carries a language prefix that differs from state, follow the URL
  useEffect(() => {
    const seg = location.pathname.split('/')[1];
    if ((seg === 'en' || seg === 'es') && seg !== lang) {
      setLangState(seg as Lang);
    }
  }, [location.pathname]);

  const path = useCallback(
    (p: string, l?: Lang) => {
      const target = l || lang;
      const clean = p.startsWith('/') ? p : `/${p}`;
      const withoutLang = clean.replace(/^\/(en|es)(?=\/|$)/, '');
      const base = withoutLang === '' ? '/' : withoutLang;
      const localized = target === 'es' ? toEsPath(toEnPath(base)) : toEnPath(base);
      return `/${target}${localized === '/' ? '' : localized}`;
    },
    [lang]
  );

  const setLang = useCallback(
    (l: Lang) => {
      if (l === lang) return;
      setFading(true);
      window.setTimeout(() => {
        setLangState(l);
        const current = location.pathname.replace(/^\/(en|es)(?=\/|$)/, '') || '/';
        const english = toEnPath(current);
        const next = l === 'es' ? `/es${toEsPath(english) === '/' ? '' : toEsPath(english)}` : `/en${english === '/' ? '' : english}`;
        navigate(next + location.search, { replace: true });
        window.setTimeout(() => setFading(false), 60);
      }, 160);
    },
    [lang, location, navigate]
  );

  /**
   * Translate a key. Every string leaves this function through `brandCase()`,
   * which is the single enforcement point for the GIGGONE wordmark across the
   * entire bilingual dictionary (nav, dashboard, admin, errors, notifications,
   * meta titles, meta descriptions, Help Center, About, FAQ — EN and ES).
   * A contributor who types the brand with legacy casing in translations.ts
   * still renders the canonical wordmark on screen and in <head>.
   */
  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      let str = dictionaries[lang][key] ?? dictionaries.en[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return brandCase(str);
    },
    [lang]
  );


  return <Ctx.Provider value={{ lang, setLang, t, path, fading }}>{children}</Ctx.Provider>;
};

export const useI18n = (): LanguageCtx => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n must be used inside LanguageProvider');
  return ctx;
};
